import { addDays, isoDate, weekday, type IsoDate } from './dates';

/**
 * DE: Bundesland als amtliches Kürzel.
 * EN: German federal state as its official code.
 */
export type FederalState =
  | 'BW'
  | 'BY'
  | 'BE'
  | 'BB'
  | 'HB'
  | 'HH'
  | 'HE'
  | 'MV'
  | 'NI'
  | 'NW'
  | 'RP'
  | 'SL'
  | 'SN'
  | 'ST'
  | 'SH'
  | 'TH';

/**
 * DE: Regel für einen gesetzlichen Feiertag: festes Datum, Abstand zu Ostern oder Buß- und Bettag.
 * EN: Rule for a public holiday: fixed date, offset from Easter or Repentance Day.
 */
export type HolidayRule = { id: string; states: 'all' | readonly FederalState[] } & (
  | { kind: 'fixed'; month: number; day: number }
  | { kind: 'easter'; offset: number }
  | { kind: 'repentance-day' }
);

/**
 * DE: Was bestimmt, an welchen Tagen die Läden geschlossen sind.
 * EN: What decides on which days the shops are closed.
 */
export interface ShopCalendar {
  federalState?: FederalState | undefined;
  closedDates: readonly IsoDate[];
  holidays: readonly HolidayRule[];
}

const SUNDAY = 7;
const WEDNESDAY = 3;

/**
 * DE: Ostersonntag im gregorianischen Kalender (Verfahren nach Meeus/Jones/Butcher).
 * EN: Easter Sunday in the Gregorian calendar (Meeus/Jones/Butcher method).
 */
export function easterSunday(year: number): IsoDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return isoDate(year, month, day);
}

function ruleDate(rule: HolidayRule, year: number): IsoDate {
  if (rule.kind === 'fixed') return isoDate(year, rule.month, rule.day);
  if (rule.kind === 'easter') return addDays(easterSunday(year), rule.offset);
  // DE: Buß- und Bettag ist der letzte Mittwoch vor dem 23. November.
  // EN: Repentance Day is the last Wednesday before 23 November.
  const lastDay = isoDate(year, 11, 22);
  return addDays(lastDay, -((weekday(lastDay) - WEDNESDAY + 7) % 7));
}

function appliesTo(rule: HolidayRule, calendar: ShopCalendar): boolean {
  return rule.states === 'all' || rule.states.some((state) => state === calendar.federalState);
}

/**
 * DE: Gesetzliche Feiertage eines Jahres: bundesweite und die des gewählten Bundeslands.
 * EN: Public holidays of a year: nationwide ones and those of the chosen federal state.
 */
export function publicHolidays(
  year: number,
  calendar: ShopCalendar,
): { date: IsoDate; id: string }[] {
  return calendar.holidays
    .filter((rule) => appliesTo(rule, calendar))
    .map((rule) => ({ date: ruleDate(rule, year), id: rule.id }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * DE: Kennung des Feiertags an diesem Tag, sonst `undefined`.
 * EN: Id of the holiday on this date, otherwise `undefined`.
 */
export function holidayOn(date: IsoDate, calendar: ShopCalendar): string | undefined {
  const year = Number(date.slice(0, 4));
  return publicHolidays(year, calendar).find((holiday) => holiday.date === date)?.id;
}

/**
 * DE: Geschlossen sind Sonntage, Feiertage und eigene Schließtage (z. B. ein lokaler Feiertag).
 * EN: Closed are Sundays, public holidays and own closed days (e.g. a local holiday).
 */
export function isClosedDay(date: IsoDate, calendar: ShopCalendar): boolean {
  return (
    weekday(date) === SUNDAY ||
    calendar.closedDates.includes(date) ||
    holidayOn(date, calendar) !== undefined
  );
}

/**
 * DE: Erster Tag ab `date`, an dem die Läden offen sind.
 * EN: First day from `date` on which the shops are open.
 */
export function firstOpenDay(date: IsoDate, calendar: ShopCalendar): IsoDate {
  let day = date;
  while (isClosedDay(day, calendar)) day = addDays(day, 1);
  return day;
}
